import { useId } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, AlertTriangle, FolderOpen } from 'lucide-react';
import { useApplicationsOverview } from '@/hooks/useApplicationsOverview';
import { applicationStatusLabel, normalizeApplicationStatus } from '../../../../shared/src/utils/applicationStatus';
import { ApplicationNextStep } from '../applications/ApplicationNextStep';

const statusColors: Record<string, string> = {
  DRAFT: 'secondary', SUBMITTED: 'info text-dark', APPROVED: 'success', REJECTED: 'danger',
  DEFERRED: 'info text-dark', WITHDRAWN: 'secondary', TUITION_PAYMENT_STAGE: 'info text-dark',
  PAID: 'success', VISA_APPLICATION: 'info text-dark', VISA_APPROVED: 'success', ENROLLED: 'success',
};

export function ApplicationsOverview() {
  const headingId = useId();
  const query = useApplicationsOverview();
  const rows = query.data?.applications;
  const retry = () => { void query.refetch({ cancelRefetch: false }); };
  return (
    <section className="card h-100 border-0 shadow-sm applications-overview" aria-labelledby={headingId}>
      <div className="card-header applications-overview__header">
        <div>
          <h5 className="card-title" id={headingId}>Applications Overview</h5>
          <p className="applications-overview__subtitle">Recently updated</p>
        </div>
        <Link to="/applications" className="applications-overview__view-all"><span className="applications-overview__view-all-desktop">View all applications</span><span className="applications-overview__view-all-mobile">View all</span></Link>
      </div>
      <div className="card-body applications-overview__body" aria-busy={query.isFetching}>
        {query.isError && (
          <div role="alert" className={rows ? "applications-overview__refresh-error" : "applications-overview__state applications-overview__state--error"}>
            {!rows && <AlertTriangle size={30} aria-hidden="true" />}
            <span>{rows ? 'Could not refresh applications. Showing the last successful update.' : 'Could not load applications.'}</span>
            <button type="button" className="applications-overview__retry" disabled={query.isFetching} onClick={retry}>Retry</button>
          </div>
        )}
        {!rows && !query.isError && <div role="status" className="applications-overview__state"><span className="applications-overview__spinner" aria-hidden="true" /><p>Loading applications</p></div>}
        {rows && query.isFetching && <p role="status" className="small text-muted mb-2">Refreshing applications…</p>}
        {rows?.length === 0 && <div className="applications-overview__state"><FolderOpen size={36} aria-hidden="true" /><p>No applications yet</p></div>}
        {rows && rows.length > 0 && (
          <ul className="list-unstyled mb-0 applications-overview__list">
            {rows.map(app => {
              const destination = `/applications/${app.id}`;
              const name = [app.firstName, app.middleName, app.lastName].filter(part => part?.trim()).join(' ') || 'Name not provided';
              const action = app.requiresResponse || app.requiresDocumentUpload;
              const reason = [app.requiresResponse && 'Response required', app.requiresDocumentUpload && 'Documents requested'].filter(Boolean).join('; ');
              return (
                <li key={app.id} className="applications-overview__row">
                  <div className="applications-overview__student">
                    <Link to={destination} className="applications-overview__name fw-semibold">{name}</Link>
                    <div className="applications-overview__metadata small text-muted">
                      <span><span className="applications-overview__field-label">Course: </span>{app.university_programs?.programs?.name || 'Not provided'}</span>
                      <span className="applications-overview__separator" aria-hidden="true"> · </span>
                      <span><span className="applications-overview__field-label">Institution: </span>{app.universities?.name || 'Not provided'}</span>
                      <span className="applications-overview__separator" aria-hidden="true"> · </span>
                      <span><span className="applications-overview__field-label">Reference: </span><Link to={destination} className="font-monospace">{app.reference || 'Not provided'}</Link></span>
                    </div>
                  </div>
                  <div className="applications-overview__workflow">
                    <div className="d-flex flex-wrap align-items-center gap-2 applications-overview__indicators">
                      {action && <span className="applications-overview__action small" title={reason}><AlertCircle size={14} aria-hidden="true" />Agent action<span className="visually-hidden">: {reason}</span></span>}
                      <span className={`badge applications-overview__status applications-overview__status--${normalizeApplicationStatus(app.status).toLowerCase()} bg-${statusColors[normalizeApplicationStatus(app.status)] || 'secondary'}`}>{applicationStatusLabel(app.status)}</span>
                    </div>
                    <ApplicationNextStep status={app.status} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
